import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useUnauthenticatedApi } from "../../hooks/useApi";

interface CancellationTimelineProps {
  className?: string;
  operatorCode?: string;
  checkinDateString?: string | null;
  checkinTimeString?: string | null;
  data?: CancellationPolicyAPI[];
  finalEmoji?: string;
  finalLabel?: string;
}

interface CancellationPolicyAPI {
  start_date?: string | null;
  end_date?: string | null;
  min_days_before_checkin: number;
  max_days_before_checkin?: number | null;
  cancellation_charge: number;
  operator: number;
}

interface TimelineNode {
  dateLabel: string;
  mainText: string;
  subText?: string;
  icon: string;
  align: "text-left" | "text-center" | "text-right";
}

interface UIDisplayStage {
  charge: number;
  periodStart: moment.Moment;
  periodEnd: moment.Moment;
  displayInfo: ReturnType<typeof getPolicyDisplayInfo>;
}

interface TimelineDisplayData {
  nodes: TimelineNode[];
  uiDisplayStages: UIDisplayStage[];
}

// Props for the new sub-components
interface TimelineDesktopProps {
  nodes: TimelineNode[];
  uiDisplayStages: UIDisplayStage[];
  numDistinctStages: number;
}

interface TimelineMobileProps {
  nodes: TimelineNode[];
  uiDisplayStages: UIDisplayStage[];
}

const getPolicyDisplayInfo = (charge: number) => {
  if (charge === 0)
    return {
      icon: "✅",
      color: "bg-green",
      label: "Free Cancellation",
    };
  if (charge === 100)
    return { icon: "😢", color: "bg-red-500", label: "No Refund" };
  return { icon: "⚠️", color: "bg-yellow-400", label: `${charge}% fee` };
};

const CancellationTimeline: React.FC<CancellationTimelineProps> = ({
  className,
  operatorCode,
  checkinDateString,
  checkinTimeString,
  data,
  finalEmoji,
  finalLabel,
}) => {
  const [cancellationData, setCancellationData] = useState<
    CancellationPolicyAPI[]
  >([]);

  const operatorApi = useUnauthenticatedApi("STAY_OPERATORS");

  useEffect(() => {
    if (!!operatorCode && !!checkinDateString) {
      operatorApi
        .get(`/${operatorCode}/cancellation-policy/`)
        .then((res) => {
          setCancellationData(res.data.cancellation_policy);
        })
        .catch(console.log);
    }
  }, [checkinDateString, operatorApi, operatorCode]);

  const timelineDisplayData = useMemo((): TimelineDisplayData | null => {
    if (!checkinDateString) return null;

    const currentDayStart = moment().startOf("day");
    const checkInDateOnly = moment(checkinDateString, "YYYY-MM-DD");
    const checkinTimeOnly = checkinTimeString
      ? moment(checkinTimeString, "HH:mm:ss")
      : null;
    const checkInTime = moment(checkInDateOnly).set({
      hour: checkinTimeOnly?.hour() || 0,
      minute: checkinTimeOnly?.minute() || 0,
      second: checkinTimeOnly?.second() || 0,
      millisecond: checkinTimeOnly?.millisecond() || 0,
    });

    const isTodayCheckInDay = currentDayStart.isSame(checkInDateOnly, "day");

    if (moment().isAfter(checkInTime)) {
      if (isTodayCheckInDay) {
        // Scenario: Check-in day, current time is past check-in time.
        const finalPolicyInfo = getPolicyDisplayInfo(100); // Assumes 100% charge
        const specificUiDisplayStages: UIDisplayStage[] = [
          {
            charge: 100,
            periodStart: currentDayStart,
            periodEnd: checkInTime, // Conceptual end
            displayInfo: { ...finalPolicyInfo, label: "Past Check-in Time" },
          },
        ];

        const specificNodes: TimelineNode[] = Array(4)
          .fill(null)
          .map(() => ({
            dateLabel: "",
            mainText: "",
            subText: undefined,
            icon: "",
            align: "text-left",
          }));
        specificNodes[0] = {
          dateLabel: `Today, ${moment().format("h:mm a")}`,
          mainText: "Past Check-in Time",
          subText: "No Refund",
          icon: finalPolicyInfo.icon,
          align: "text-left",
        };
        specificNodes[1] = {
          dateLabel: "",
          mainText: "",
          subText: undefined,
          icon: "",
          align: "text-center",
        };
        specificNodes[2] = {
          dateLabel: "",
          mainText: "",
          subText: undefined,
          icon: "",
          align: "text-center",
        };
        specificNodes[3] = {
          dateLabel: checkInTime.format("D MMM"),
          mainText: `Check-in was ${checkInTime.format("h:mm a")}`,
          icon: finalEmoji ?? "🏨",
          align: "text-right",
        };
        return {
          nodes: specificNodes,
          uiDisplayStages: specificUiDisplayStages,
        };
      } else {
        // Not check-in day, and current time is past the check-in time for that (past) day
        return null;
      }
    }

    const apiPolicies = cancellationData.length > 0 ? cancellationData : data;
    const policiesInput =
      apiPolicies && apiPolicies.length > 0 ? apiPolicies : [];

    const sortedPolicies = [...policiesInput].sort(
      (a, b) =>
        (a.min_days_before_checkin ?? Infinity) -
        (b.min_days_before_checkin ?? Infinity)
    );

    const relevantRawPolicies: UIDisplayStage[] = [];
    for (const policy of sortedPolicies) {
      const policyEffectiveUntil = moment(checkInTime)
        .subtract(policy.min_days_before_checkin, "days")
        .set({ hour: 23, minute: 59, second: 59 });

      if (
        moment().isAfter(policyEffectiveUntil) &&
        !policyEffectiveUntil.isSame(currentDayStart, "day")
      ) {
        continue;
      }
      const policyEffectiveFrom =
        policy.max_days_before_checkin != null
          ? moment(checkInTime)
              .subtract(policy.max_days_before_checkin, "days")
              .startOf("day")
          : moment(0); // Unix epoch

      relevantRawPolicies.push({
        charge: policy.cancellation_charge,
        periodStart: policyEffectiveFrom,
        periodEnd: policyEffectiveUntil,
        displayInfo: getPolicyDisplayInfo(policy.cancellation_charge),
      });
    }
    relevantRawPolicies.reverse();

    const uiDisplayStages: UIDisplayStage[] = [];
    if (relevantRawPolicies.length > 0) {
      for (const p of relevantRawPolicies) {
        if (
          uiDisplayStages.length === 0 ||
          p.charge !== uiDisplayStages[uiDisplayStages.length - 1].charge
        ) {
          if (uiDisplayStages.length < 3) {
            uiDisplayStages.push(p);
          }
        }
      }
    }

    if (uiDisplayStages.length === 0) {
      uiDisplayStages.push({
        charge: 100,
        periodStart: currentDayStart,
        periodEnd: moment(checkInTime)
          .subtract(1, "days")
          .set({ hour: 23, minute: 59, second: 59 }),
        displayInfo: getPolicyDisplayInfo(100),
      });
    }

    const nodes: TimelineNode[] = Array(4)
      .fill(null)
      .map(() => ({
        dateLabel: "",
        mainText: "",
        subText: undefined,
        icon: "",
        align: "text-left",
      }));

    nodes[3] = {
      dateLabel: checkInTime.format("D MMM h:mm a"),
      mainText: finalLabel ?? "Check-in day",
      icon: finalEmoji ?? "🏨",
      subText: undefined,
      align: "text-right",
    };

    if (uiDisplayStages.length > 0) {
      const stage0 = uiDisplayStages[0];
      const endDateForStage0 = !uiDisplayStages[1]
        ? checkInTime.clone().subtract(1, "second")
        : stage0.periodEnd;

      nodes[0] = {
        dateLabel: `Now → ${endDateForStage0.format("D MMM h:mm A")}`,
        mainText: stage0.displayInfo.label,
        icon: stage0.displayInfo.icon,
        align: "text-left",
      };

      if (uiDisplayStages[1]) {
        const stage1 = uiDisplayStages[1];
        const startDateForStage1 = moment(stage0.periodEnd)
          .add(1, "day")
          .startOf("day");
        const endDateForStage1 = !uiDisplayStages[2]
          ? checkInTime.clone().subtract(1, "second")
          : stage1.periodEnd;

        nodes[1] = {
          dateLabel: `${startDateForStage1.format(
            "D MMM h:mm A"
          )} → ${endDateForStage1.format("D MMM h:mm A")}`,
          mainText: stage1.displayInfo.label,
          icon: stage1.displayInfo.icon,
          align: "text-left",
        };
      }

      if (uiDisplayStages[2]) {
        const stage2 = uiDisplayStages[2];
        const startDateForStage2 = moment(uiDisplayStages[1].periodEnd)
          .add(1, "day")
          .startOf("day");

        nodes[2] = {
          dateLabel: `${startDateForStage2.format(
            "D MMM h:mm A"
          )} → ${checkInTime
            .clone()
            .subtract(1, "second")
            .format("D MMM h:mm A")}`,
          mainText: stage2.displayInfo.label,
          icon: stage2.displayInfo.icon,
          align: "text-left",
        };
      }
    }

    if (isTodayCheckInDay && nodes[0] && uiDisplayStages.length > 0) {
      nodes[0].dateLabel = `Today → ${checkInTime.format("h:mm A")}`;
    }

    return { nodes, uiDisplayStages };
  }, [
    checkinDateString,
    checkinTimeString,
    cancellationData,
    data,
    finalLabel,
    finalEmoji,
  ]);

  if (!timelineDisplayData) {
    return (
      <div className={`my-6 ${className || ""}`.trim()}>
        Loading cancellation policy or check-in date past...
      </div>
    );
  }

  const { nodes, uiDisplayStages } = timelineDisplayData;
  const numDistinctStages = uiDisplayStages.length;

  return (
    <div className={`${className || ""}`.trim()}>
      {/* Desktop View */}
      <div className="hidden lg:block">
        <TimelineDesktop
          nodes={nodes}
          uiDisplayStages={uiDisplayStages}
          numDistinctStages={numDistinctStages}
        />
      </div>

      {/* Mobile View */}
      <div className="block lg:hidden space-y-0 px-4 py-2">
        <TimelineMobile nodes={nodes} uiDisplayStages={uiDisplayStages} />
      </div>
    </div>
  );
};

// Desktop Timeline Component
const TimelineDesktop: React.FC<TimelineDesktopProps> = ({
  nodes,
  uiDisplayStages,
  numDistinctStages,
}) => {
  return (
    <>
      {/* Row 1: Icons and Bars - Dynamically Rendered */}
      <div className="flex items-center">
        {/* Icon 0 (Stage 0) - Always shown if uiDisplayStages has items */}
        {numDistinctStages > 0 && (
          <div
            className={`text-base px-2 ${
              uiDisplayStages[0].displayInfo.icon === "✅"
                ? "text-green"
                : uiDisplayStages[0].displayInfo.icon === "⚠️"
                ? "text-yellow-500"
                : uiDisplayStages[0].displayInfo.icon === "😢"
                ? "text-red-500"
                : "text-gray-700"
            }`}
          >
            {uiDisplayStages[0].displayInfo.icon}
          </div>
        )}

        {/* Bar after Stage 0 */}
        {numDistinctStages === 1 && (
          <div
            className={`h-2 rounded-full ${uiDisplayStages[0].displayInfo.color}`}
            style={{ flexGrow: 4 }}
          ></div>
        )}
        {numDistinctStages > 1 && (
          <div
            className={`h-2 rounded-full flex-grow ${uiDisplayStages[0].displayInfo.color}`}
          ></div>
        )}

        {/* Icon 1 (Stage 1) - Shown if numDistinctStages > 1 */}
        {numDistinctStages > 1 && (
          <>
            <div
              className={`text-base px-2 ${
                uiDisplayStages[1].displayInfo.icon === "✅"
                  ? "text-green"
                  : uiDisplayStages[1].displayInfo.icon === "⚠️"
                  ? "text-yellow-500"
                  : uiDisplayStages[1].displayInfo.icon === "😢"
                  ? "text-red-500"
                  : "text-gray-700"
              }`}
            >
              {uiDisplayStages[1].displayInfo.icon}
            </div>
            {/* Bar after Stage 1 */}
            {numDistinctStages === 2 && (
              <div
                className={`h-2 rounded-full ${uiDisplayStages[1].displayInfo.color}`}
                style={{ flexGrow: 3 }}
              ></div>
            )}
            {numDistinctStages > 2 && (
              <div
                className={`h-2 rounded-full flex-grow ${uiDisplayStages[1].displayInfo.color}`}
              ></div>
            )}
          </>
        )}

        {/* Icon 2 (Stage 2) - Shown if numDistinctStages > 2 */}
        {numDistinctStages > 2 && (
          <>
            <div
              className={`text-base px-2 ${
                uiDisplayStages[2].displayInfo.icon === "✅"
                  ? "text-green"
                  : uiDisplayStages[2].displayInfo.icon === "⚠️"
                  ? "text-yellow-500"
                  : uiDisplayStages[2].displayInfo.icon === "😢"
                  ? "text-red-500"
                  : "text-gray-700"
              }`}
            >
              {uiDisplayStages[2].displayInfo.icon}
            </div>
            {/* Bar after Stage 2 */}
            <div
              className={`h-2 rounded-full ${uiDisplayStages[2].displayInfo.color}`}
              style={{ flexGrow: 2 }}
            ></div>
          </>
        )}

        {/* Icon 3 (Check-in) - Always shown */}
        <div className={`text-base px-2 text-gray-700`}>{nodes[3].icon}</div>
      </div>

      {/* Row 2: Text Descriptions (uses the 4-slot nodes array) */}
      <div className="grid grid-cols-4 gap-x-2 mt-2">
        {nodes.map((node, index) => (
          <div
            key={index}
            className={`px-2 text-light-text-secondary ${node.align}`}
          >
            {node.dateLabel && (
              <p className="text-xs xl:whitespace-no-wrap">{node.dateLabel}</p>
            )}
            {node.mainText && (
              <p className="text-light-text-primary text-sm font-semibold">
                <span
                  className={`${
                    node.mainText.includes("%") ||
                    node.mainText.toLowerCase().includes("no refund") ||
                    node.mainText.includes("Check-in")
                      ? "font-bold"
                      : ""
                  }`.trim()}
                >
                  {node.mainText.split(" ")[0]}
                </span>{" "}
                {node.mainText.substring(node.mainText.indexOf(" ") + 1)}
                {node.subText && (
                  <>
                    <br />
                    <span className="text-xs font-normal text-gray-500">
                      {node.subText}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// Mobile Timeline Component
const TimelineMobile: React.FC<TimelineMobileProps> = ({
  nodes,
  uiDisplayStages,
}) => {
  return (
    <>
      {(() => {
        const nodesWithOriginalIndex = nodes.map((node, index) => ({
          node,
          originalIndex: index,
        }));
        const activeMobileNodesData = nodesWithOriginalIndex.filter(
          ({ node }) => node.icon || node.dateLabel || node.mainText
        );

        return activeMobileNodesData.map(
          ({ node, originalIndex }, activeIdx) => {
            const barColorClass =
              uiDisplayStages[originalIndex]?.displayInfo.color ??
              "bg-gray-300";

            const currentMainText = node.mainText || "";
            const firstSpaceIndex = currentMainText.indexOf(" ");
            const firstWord =
              firstSpaceIndex === -1
                ? currentMainText
                : currentMainText.substring(0, firstSpaceIndex);
            const remainingText =
              firstSpaceIndex === -1
                ? ""
                : currentMainText.substring(firstSpaceIndex + 1);

            const isMainTextBold =
              currentMainText &&
              (currentMainText.includes("%") ||
                currentMainText.toLowerCase().includes("no refund") ||
                currentMainText.toLowerCase().includes("check-in day"));

            const showLine = activeIdx < activeMobileNodesData.length - 1;

            return (
              <div key={originalIndex} className="flex items-start">
                <div className="flex flex-col items-center w-10 mr-2 shrink-0">
                  <div
                    className={`text-xl ${
                      node.icon === "✅"
                        ? "text-green"
                        : node.icon === "⚠️"
                        ? "text-yellow-500"
                        : node.icon === "😢"
                        ? "text-red-500"
                        : "text-gray-700" // Default for hotel icon or others
                    }`}
                  >
                    {node.icon || "\\\\u00A0"}{" "}
                    {/* Non-breaking space if no icon, to maintain layout */}
                  </div>
                  {showLine && (
                    <div className={`w-1 h-16 ${barColorClass}`}></div>
                  )}
                </div>
                <div className="text-sm pt-1 flex-grow">
                  {node.dateLabel && (
                    <p className="text-xs text-light-text-secondary mb-1">
                      {node.dateLabel}
                    </p>
                  )}
                  {currentMainText && (
                    <p
                      className={`text-light-text-primary font-semibold ${
                        node.dateLabel ? "" : "mt-0"
                      }`}
                    >
                      <span
                        className={`${
                          isMainTextBold ? "font-bold" : ""
                        }`.trim()}
                      >
                        {firstWord}
                      </span>
                      {remainingText && ` ${remainingText}`}
                    </p>
                  )}
                  {node.subText && (
                    <p className="text-xs text-gray-500 mt-1">{node.subText}</p>
                  )}
                </div>
              </div>
            );
          }
        );
      })()}
    </>
  );
};

export default CancellationTimeline;
